import React, { useEffect, useMemo, useRef, useState } from 'react'
import './DocumentsSection.css'
import httpClient from './helpers/httpClient'
import { toast } from 'react-toastify'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PopupDropdown from './PopupDropdown'
import { MdClose } from 'react-icons/md'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { formatDate } from './helpers/formatDate'

function DocumentsSection({
  scope,
  entityId,
  editable = true,
  rentalContractId = null,
  defaultTypeName = null,
  showRentalContractSelect = false,
}) {
  const [documents, setDocuments] = useState([])
  const [documentTypes, setDocumentTypes] = useState([])
  const [rentalContracts, setRentalContracts] = useState([])
  const [loading, setLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [selectedType, setSelectedType] = useState(null)
  const [selectedRentalContract, setSelectedRentalContract] = useState(null)
  const [newTypeName, setNewTypeName] = useState('')
  const [isSavingType, setIsSavingType] = useState(false)
  const fileInputRef = useRef(null)

  const canInteract = editable && Boolean(entityId)

  const acceptedFileTypes = useMemo(
    () => '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp',
    []
  )

  const fetchDocumentTypes = async () => {
    if (!scope) return []

    const response = await httpClient.get(
      `${import.meta.env.VITE_API_URL}/api/document-types?scope=${scope}`
    )
    const types = response.data || []
    setDocumentTypes(types)
    return types
  }

  const fetchRentalContracts = async () => {
    if (scope !== 'unit' || !entityId || !showRentalContractSelect) return []

    try {
      const response = await httpClient.get(
        `${import.meta.env.VITE_API_URL}/api/rental-contracts?unit_id=${entityId}&limit=100`
      )
      const contracts = (response.data?.rental_contracts || []).map((contract) => ({
        ...contract,
        name: `Contract ${contract.id}${formatDate(contract.start_date) ? ` - ${formatDate(contract.start_date)}` : ''}`,
      }))
      setRentalContracts(contracts)
      return contracts
    } catch (error) {
      console.error('Error fetching rental contracts:', error)
      setRentalContracts([])
      return []
    }
  }

  const fetchDocuments = async () => {
    if (!scope || !entityId) return

    setLoading(true)
    try {
      const params = new URLSearchParams({ scope, entity_id: entityId })
      if (rentalContractId) params.append('rental_contract_id', rentalContractId)
      const response = await httpClient.get(
        `${import.meta.env.VITE_API_URL}/api/documents?${params.toString()}`
      )
      setDocuments(response.data || [])
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!entityId) {
      setDocuments([])
      return
    }

    const loadDocumentTypes = async () => {
      const types = await fetchDocumentTypes()
      if (defaultTypeName) {
        const defaultType = types.find(
          (type) => type.name?.toLowerCase() === defaultTypeName.toLowerCase()
        )
        if (defaultType) setSelectedType(defaultType)
      }
    }

    loadDocumentTypes()
    fetchRentalContracts()
    fetchDocuments()
  }, [scope, entityId, rentalContractId, defaultTypeName, showRentalContractSelect])

  const resetUploadState = () => {
    setPendingFiles([])
    const defaultType = defaultTypeName
      ? documentTypes.find((type) => type.name?.toLowerCase() === defaultTypeName.toLowerCase())
      : null
    setSelectedType(defaultType || null)
    setSelectedRentalContract(null)
    setNewTypeName('')
    setIsUploadModalOpen(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openUploadModal = (fileList) => {
    if (!canInteract || !fileList?.length) return
    setPendingFiles(Array.from(fileList))
    const defaultType = defaultTypeName
      ? documentTypes.find((type) => type.name?.toLowerCase() === defaultTypeName.toLowerCase())
      : null
    setSelectedType(defaultType || null)
    setSelectedRentalContract(
      rentalContractId
        ? rentalContracts.find((contract) => String(contract.id) === String(rentalContractId)) || null
        : null
    )
    setNewTypeName('')
    setIsUploadModalOpen(true)
  }

  const handleFileInputChange = (event) => {
    openUploadModal(event.target.files)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)
    openUploadModal(event.dataTransfer.files)
  }

  const handleDragEvent = (event, active) => {
    event.preventDefault()
    event.stopPropagation()
    if (!canInteract) return
    setDragActive(active)
  }

  const handleCreateType = async () => {
    const trimmedName = newTypeName.trim()
    if (!trimmedName) {
      toast.error('Please enter a document type name')
      return
    }

    setIsSavingType(true)
    try {
      const response = await toast.promise(
        httpClient.post(`${import.meta.env.VITE_API_URL}/api/document-types/create`, {
          name: trimmedName,
          scope,
        }),
        {
          pending: 'Creating document type...',
          success: 'Document type created',
          error: 'Failed to create document type',
        }
      )

      const createdType = response.data
      setDocumentTypes((prev) => {
        const nextTypes = [...prev.filter((item) => item.id !== createdType.id), createdType]
        return nextTypes.sort((a, b) => a.name.localeCompare(b.name))
      })
      setSelectedType(createdType)
      setNewTypeName('')
    } catch (error) {
      console.error('Error creating document type:', error)
    } finally {
      setIsSavingType(false)
    }
  }

  const handleUploadDocuments = async () => {
    if (!selectedType?.id) {
      toast.error('Please select a document type')
      return
    }

    const formData = new FormData()
    pendingFiles.forEach((file) => formData.append('files[]', file))
    formData.append('scope', scope)
    formData.append('entity_id', entityId)
    formData.append('document_type_id', selectedType.id)
    const uploadRentalContractId = rentalContractId || selectedRentalContract?.id
    if (uploadRentalContractId) {
      formData.append('rental_contract_id', uploadRentalContractId)
    }

    try {
      await toast.promise(
        httpClient.post(`${import.meta.env.VITE_API_URL}/api/documents/upload`, formData),
        {
          pending: 'Uploading documents...',
          success: 'Documents uploaded successfully',
          error: 'Failed to upload documents',
        }
      )
      resetUploadState()
      fetchDocuments()
    } catch (error) {
      console.error('Error uploading documents:', error)
    }
  }

  const handleDeleteDocument = async (documentId) => {
    try {
      await toast.promise(
        httpClient.delete(`${import.meta.env.VITE_API_URL}/api/documents/${documentId}`),
        {
          pending: 'Deleting document...',
          success: 'Document deleted',
          error: 'Failed to delete document',
        }
      )
      fetchDocuments()
    } catch (error) {
      console.error('Error deleting document:', error)
    }
  }

  const handleUpdateDocumentType = async (documentId, nextType, nextRentalContractId) => {
    if (!nextType?.id) return

    try {
      const response = await httpClient.put(
        `${import.meta.env.VITE_API_URL}/api/documents/${documentId}/type`,
        {
          document_type_id: nextType.id,
          ...(nextRentalContractId !== undefined ? { rental_contract_id: nextRentalContractId } : {}),
        }
      )

      setDocuments((prev) =>
        prev.map((document) => (document.id === documentId ? response.data : document))
      )
      toast.success('Document type updated')
    } catch (error) {
      console.error('Error updating document type:', error)
      toast.error('Failed to update document type')
    }
  }

  const selectedTypeIsRentalContract = selectedType?.name?.toLowerCase() === 'rental contract'

  return (
    <div className="documents-section">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes}
        className="documents-hidden-input"
        onChange={handleFileInputChange}
      />

      {canInteract && (
        <div
          className={`documents-dropzone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={(event) => handleDragEvent(event, true)}
          onDragOver={(event) => handleDragEvent(event, true)}
          onDragLeave={(event) => handleDragEvent(event, false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="documents-dropzone__title">Drag and drop files here</div>
          <div className="documents-dropzone__subtitle">or click to browse</div>
        </div>
      )}

      <div className="documents-list">
        {loading ? (
          <div className="documents-loading">Loading documents...</div>
        ) : (
          documents.map((document) => (
            <div key={document.id} className="documents-list__item">
              <a href={document.file_url} target="_blank" rel="noreferrer" className="documents-list__link">
                <div className="documents-list__icon">
                  <FileDownloadIcon fontSize="small" />
                </div>
                <div className="documents-list__meta">
                  <span className="documents-list__name">{document.file_name || 'Document'}</span>
                  <span className="documents-list__type">{document.document_type?.name || 'Uncategorized'}</span>
                </div>
              </a>

              <div className="documents-list__actions">
                {editable && (
                  <PopupDropdown
                    options={documentTypes}
                    value={document.document_type || null}
                    onChange={(nextType) => handleUpdateDocumentType(document.id, nextType)}
                    title="Select document type"
                    searchBar={true}
                    width="420px"
                    customButton={
                      <button className="documents-edit-button" title="Edit document type">
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                    }
                  />
                )}
                {editable && (
                  <button
                    className="documents-delete-button"
                    onClick={() => handleDeleteDocument(document.id)}
                    title="Delete document"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isUploadModalOpen && (
        <div className="documents-modal-overlay" onClick={resetUploadState}>
          <div className="documents-modal" onClick={(event) => event.stopPropagation()}>
            <div className="documents-modal-header">
              <h3>Upload documents</h3>
              <button className="documents-modal__close-button" onClick={resetUploadState}>
                <MdClose />
              </button>
            </div>

            <div className="documents-modal__files">
              {pendingFiles.map((file) => (
                <div key={`${file.name}-${file.lastModified}`} className="documents-modal__file-pill">
                  {file.name}
                </div>
              ))}
            </div>

            <div className="documents-modal__section">
              <label className="documents-modal__label">Document type</label>
              <div className="documents-modal__type-select">
                <PopupDropdown
                  options={documentTypes}
                  value={selectedType}
                  onChange={setSelectedType}
                  title="Select document type"
                  searchBar={true}
                  width="420px"
                />
              </div>
            </div>

            {showRentalContractSelect && selectedTypeIsRentalContract && (
              <div className="documents-modal__section">
                <label className="documents-modal__label">Rental contract</label>
                <div className="documents-modal__type-select">
                  <PopupDropdown
                    options={rentalContracts}
                    value={selectedRentalContract}
                    onChange={setSelectedRentalContract}
                    title="Select rental contract"
                    searchBar={true}
                    width="420px"
                    disabled={Boolean(rentalContractId)}
                  />
                </div>
              </div>
            )}

            <div className="documents-modal__section">
              <label className="documents-modal__label">Create new type</label>
              <div className="documents-modal__create-row">
                <input
                  type="text"
                  value={newTypeName}
                  onChange={(event) => setNewTypeName(event.target.value)}
                  placeholder={`New ${scope} document type`}
                  className="input_field__input_box--text"
                />
                <button
                  className="documents-modal__create-button"
                  onClick={handleCreateType}
                  disabled={isSavingType}
                >
                  Create
                </button>
              </div>
            </div>

            <div className="documents-modal__actions">
              <button className="documents-modal__cancel-button" onClick={resetUploadState}>Cancel</button>
              <button className="documents-modal__save-button" onClick={handleUploadDocuments}>Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentsSection
